/**
 * Come si scrive un numero, una data e un'ora in italiano — una volta sola.
 *
 * Il sistema di progetto lo impone (§11): «una sola implementazione condivisa,
 * nessuna formattazione scritta a mano nei componenti». Non è pedanteria di
 * stile. Nel sistema attuale la stessa data compare come `21/03/2026` in una
 * schermata, `2026-03-21` in un'altra e `Mar 21, 2026` in un PDF, perché tre
 * persone diverse hanno risolto tre volte lo stesso problema; e un importo
 * scritto `1234.56` accanto a uno scritto `1.234,56 €` fa dubitare del numero,
 * non della formattazione.
 *
 * Il modulo sta in `packages/shared` perché i tre consumatori — API, pannello
 * web, app — devono dire le stesse cose nello stesso modo. Usa solo `Intl`, che
 * c'è in Node, nei browser e in Hermes con ICU completo: nessuna dipendenza, e
 * nessuna tabella di traduzione da mantenere.
 */
/** Il fuso aziendale. Tutto è in UTC nel database e reso qui. */
export const COMPANY_ZONE = 'Europe/Rome';
/**
 * La lingua di partenza: quella dei processi che ne mostrano una sola.
 *
 * Qui c'era scritto «Una sola, finché §14.4 non dice diversamente», e §14.4 nel
 * frattempo ha detto diversamente: la app mostra italiano, inglese e cinese. Le
 * frasi sono state tradotte, i numeri no — e la differenza fra le due cose è che
 * una frase non tradotta **si vede**, mentre un numero non tradotto si legge lo
 * stesso, e sbagliato.
 *
 * L'ha trovato un'anteprima, non un test. `11-documenti-scuro-en` mostrava la
 * busta paga di «July» con accanto «From 03/08/2026»: in italiano è il 3 agosto,
 * in inglese si legge 3 marzo, cioè cinque mesi prima del periodo che quella
 * busta copre. Chi legge non ha modo di accorgersene: le cifre ci sono tutte e
 * sono quelle giuste.
 *
 * La costante resta perché resta vera dov'era vera: l'API e il pannello web una
 * lingua sola ce l'hanno davvero, e non chiamano niente che cambi questa.
 */
export const LOCALE = 'it-IT';
/**
 * Da lingua a locale, e la scelta che non è ovvia.
 *
 * `en-GB` e non `en-US`: fra i due cambia l'ordine di giorno e mese, e non
 * esiste un ordine che vada bene a tutti — `08/03/2026` è l'8 marzo per un
 * inglese e il 3 agosto per un americano. Scegliere il locale europeo sposta il
 * problema su chi legge americano invece di risolverlo; la risposta vera è
 * `DATA_CORTA` qui sotto, che in inglese toglie di mezzo la cifra del mese.
 *
 * `zh-CN` scrive anno-mese-giorno e non è ambiguo per nessuno.
 */
const LOCALI = { it: 'it-IT', en: 'en-GB', zh: 'zh-CN' };
/**
 * La data breve, e perché non ha la stessa forma in tutte e tre le lingue.
 *
 * Tradurre una data numerica non serve a niente: `03/08/2026` tradotto resta
 * `03/08/2026`, cioè resta una cifra che vuol dire due cose diverse secondo chi
 * la guarda. In inglese quindi il mese si scrive a lettere — `03 Aug 2026` — e
 * l'ambiguità sparisce invece di spostarsi su un'altra convenzione.
 *
 * La larghezza resta fissa, che era il motivo per cui la forma numerica era
 * stata scelta: in una colonna le righe devono restare incolonnate, e `Aug` e
 * `Sep` occupano lo stesso spazio di `08` e `09` a meno di un'inezia. Undici
 * mesi su dodici ce la fanno da soli; il dodicesimo lo raddrizza
 * `accorciaSettembre` qui sotto, e senza quello questa frase sarebbe falsa.
 */
const DATA_CORTA = {
    it: { day: '2-digit', month: '2-digit', year: 'numeric' },
    en: { day: '2-digit', month: 'short', year: 'numeric' },
    zh: { year: 'numeric', month: '2-digit', day: '2-digit' },
};
/**
 * `DATA_CORTA` senza l'anno, per le due date di un periodo su una riga sola —
 * dove l'anno è già scritto nel titolo e ripeterlo due volte allarga la riga
 * senza dire niente.
 *
 * ## Perché esiste, invece di tagliare la chiave a fette
 *
 * Perché la fetta era `chiave.slice(8, 10) + '/' + chiave.slice(5, 7)`, cioè
 * giorno-barra-mese in **tutte e tre** le lingue, ed è esattamente l'ambiguità
 * che `LOCALI` e `DATA_CORTA` qui sopra esistono per togliere. Il conto tornava
 * in italiano e in nessun'altra lingua: `07/09` con davanti `周一` si legge
 * «lunedì 9 luglio», e il 9 luglio 2026 è un giovedì. La sigla del giorno —
 * messa lì apposta perché chi scambia il mese per il giorno se ne accorga —
 * suonava come un errore su una data giusta, a ogni periodo, per due lingue su
 * tre.
 *
 * Chi scrisse la fetta lo motivò così: un formato in più «sarebbe un formato in
 * più da tenere allineato in tre lingue per due date su una riga sola». È il
 * ragionamento capovolto: tenerlo allineato in tre lingue era il lavoro, e la
 * fetta non lo evitava — lo dava per fatto sbagliandolo. Un formato che le tre
 * lingue si dividono costa cinque righe qui e zero in ogni chiamante.
 *
 * In inglese il mese resta a lettere per la stessa ragione di `DATA_CORTA`, e la
 * larghezza la tiene `accorciaSettembre` qui sotto — non `Intl` da solo, come
 * questa riga diceva prima.
 */
const DATA_GIORNO_MESE = {
    it: { day: '2-digit', month: '2-digit' },
    en: { day: '2-digit', month: 'short' },
    zh: { month: '2-digit', day: '2-digit' },
};
/**
 * Il mese che esce di una lettera più lungo degli altri undici.
 *
 * `month: 'short'` in `en-GB` dà `Jan Feb Mar Apr May Jun Jul Aug Sept Oct Nov
 * Dec`: undici abbreviazioni di tre lettere e una di quattro. Non è un capriccio
 * del motore, è il CLDR — l'inglese britannico abbrevia settembre con `Sept`
 * perché `Sep` si confonde a voce con niente ma per iscritto è tradizione
 * britannica scriverlo lungo. `en-US` scrive `Sep`, e infatti la larghezza
 * variabile è una delle cose che si sarebbero prese cambiando locale, se
 * `LOCALI` non avesse buone ragioni per stare su `en-GB`.
 *
 * Il difetto che ne veniva era piccolo e permanente: in una colonna di date, una
 * riga su dodici sporgeva di un carattere. Nessun errore, nessun test rosso, e a
 * schermo un'incolonnatura che si rompe solo a settembre — cioè il tipo di cosa
 * che si guarda per un anno senza capire cosa non torna. E i due commenti qui
 * sopra, che davano la larghezza fissa come il motivo per cui l'inglese scrive
 * il mese a lettere, erano falsi undici dodicesimi dell'anno e veri il resto.
 *
 * Il taglio è sicuro perché nei due formati che lo usano — `DATA_CORTA` e
 * `DATA_GIORNO_MESE` — accanto al mese ci sono solo cifre, spazi e virgole: la
 * stringa `Sept` in quelle uscite **non può essere** altro che il mese. Non
 * vale per `dataLunga`, che usa `month: 'long'` e scrive `September` intero, e
 * infatti quel formato non passa di qui: un'intestazione non si incolonna con
 * nessuno e il mese per esteso lì è quello che si vuole.
 */
function accorciaSettembre(s) {
    return s.replace('Sept', 'Sep');
}
/** Le altre due lingue non hanno niente da raddrizzare: il mese è due cifre. */
function cosiComE(s) {
    return s;
}
/**
 * L'orologio di Roma smontato nelle sue parti. Non si mostra a nessuno: serve
 * solo a misurare quanto Roma stia avanti rispetto a UTC in un dato istante, che
 * è l'unica cosa che `Intl` non restituisce come numero.
 */
const zoneFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: COMPANY_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
});
/** Due cifre per i minuti dentro «7h 05m»: allinea le durate incolonnate. */
const due = (m) => String(m).padStart(2, '0');
const UNITA_DURATA = {
    it: { minuti: (m) => `${m}m`, ore: (h) => `${h}h`, oreEMinuti: (h, m) => `${h}h ${due(m)}m` },
    en: { minuti: (m) => `${m}m`, ore: (h) => `${h}h`, oreEMinuti: (h, m) => `${h}h ${due(m)}m` },
    // Senza spazio e senza lo zero davanti: il cinese scrive «1小时5分», e la
    // cifra tenuta a due non allinea niente perché i caratteri prima sono larghi
    // il doppio di una cifra.
    zh: { minuti: (m) => `${m}分`, ore: (h) => `${h}小时`, oreEMinuti: (h, m) => `${h}小时${m}分` },
};
/**
 * La data lunga e il giorno della settimana in cinese, scritti a mano.
 *
 * Stessa famiglia di `UNITA_DURATA`: dove Hermes non ce la fa, la lingua se la
 * scrive da sé invece di aspettarsi da `Intl` una cosa che sul telefono non
 * arriva. Hermes porta `Intl.DateTimeFormat` **nativo** (vedi `intl.ts`), e su
 * questo Android nativo risolve `it-IT` ed `en-GB` ma per `zh-CN` **ripiega
 * sull'italiano** sui campi con un *nome* — `month: 'long'` e `weekday: 'long'`:
 * «上次修改：26 agosto 2026» al posto di «2026年8月26日», e «lunedì» al posto di
 * «星期一». Si vede solo sul telefono, solo in cinese, su una data qualunque —
 * la coda della famiglia di [[K75]] (§78, §141), trovata provando l'app (§84.6).
 *
 * Il cinese di *nomi* non ne ha bisogno: il mese è un numero seguito da 月, e i
 * giorni della settimana sono sette parole fisse. Le date **numeriche** restano
 * a `Intl` (`data`, `giornoMese`: solo cifre, che Hermes formatta in zh senza
 * ripiegare), e l'italiano e l'inglese restano a `Intl` per i loro nomi, che sul
 * telefono ci sono. Solo il cinese dei due campi con un nome passa di qui.
 *
 * I numeri si prendono nel fuso dell'azienda con un formattatore **numerico** —
 * che Hermes onora in ogni lingua perché non chiede nessun nome — e l'indice del
 * giorno da `getUTCDay` sulla data-calendario di Roma, senza fidarsi di un nome
 * inglese da riconoscere a stringa. Le cifre accanto agli ideogrammi senza
 * spazio sono la convenzione di `UNITA_DURATA` (§142): «8月», non «8 月».
 */
const CINESE_GIORNI = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
/** Le parti numeriche di una data nel fuso dell'azienda: anno, mese, giorno. */
const parteNumerica = new Intl.DateTimeFormat('en-CA', {
    timeZone: COMPANY_ZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
});
function partiRoma(d) {
    let anno = 0;
    let mese = 0;
    let giorno = 0;
    for (const parte of parteNumerica.formatToParts(d)) {
        if (parte.type === 'year')
            anno = Number(parte.value);
        else if (parte.type === 'month')
            mese = Number(parte.value);
        else if (parte.type === 'day')
            giorno = Number(parte.value);
    }
    return { anno, mese, giorno };
}
/** «2026年8月26日»: numeri e ideogrammi, nessun nome di mese da localizzare. */
function dataLungaCinese(d) {
    const { anno, mese, giorno } = partiRoma(d);
    return `${anno}年${mese}月${giorno}日`;
}
/** «星期一»: l'indice del giorno dalla data-calendario di Roma, non da un nome. */
function giornoSettimanaCinese(d) {
    const { anno, mese, giorno } = partiRoma(d);
    return CINESE_GIORNI[new Date(Date.UTC(anno, mese - 1, giorno)).getUTCDay()];
}
function costruisci(l) {
    const loc = LOCALI[l];
    const numero = (options) => new Intl.NumberFormat(loc, options);
    // I due con un *nome* (mese, giorno della settimana): in italiano e inglese li
    // fa `Intl`, in cinese li scrive a mano — vedi `dataLungaCinese`, che Hermes
    // sul telefono per zh ripiegava sull'italiano.
    const lungaIntl = new Intl.DateTimeFormat(loc, {
        timeZone: COMPANY_ZONE,
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
    const settimanaIntl = new Intl.DateTimeFormat(loc, { timeZone: COMPANY_ZONE, weekday: 'long' });
    return {
        data: new Intl.DateTimeFormat(loc, { timeZone: COMPANY_ZONE, ...DATA_CORTA[l] }),
        giornoMese: new Intl.DateTimeFormat(loc, { timeZone: COMPANY_ZONE, ...DATA_GIORNO_MESE[l] }),
        dataLunga: l === 'zh' ? dataLungaCinese : (d) => lungaIntl.format(d),
        ora: new Intl.DateTimeFormat(loc, {
            timeZone: COMPANY_ZONE,
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
        }),
        giornoSettimana: l === 'zh' ? giornoSettimanaCinese : (d) => settimanaIntl.format(d),
        relativo: new Intl.RelativeTimeFormat(loc, { numeric: 'auto' }),
        soldi: numero({
            style: 'currency',
            currency: 'EUR',
            currencyDisplay: 'symbol',
            useGrouping: 'always',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }),
        decimale: numero({ useGrouping: 'always', maximumFractionDigits: 2 }),
        secondi: numero({ useGrouping: 'always', minimumFractionDigits: 1, maximumFractionDigits: 1 }),
        intero: numero({ useGrouping: 'always', maximumFractionDigits: 0 }),
        elenco: new Intl.ListFormat(loc, { style: 'long', type: 'conjunction' }),
        durata: UNITA_DURATA[l],
        raddrizza: l === 'en' ? accorciaSettembre : cosiComE,
    };
}
const gruppi = new Map();
/**
 * Il gruppo in uso adesso: una variabile e non una ricerca nella mappa, perché
 * la si legge a ogni singola formattazione.
 */
let fmt = ricava('it');
function ricava(l) {
    const gia = gruppi.get(l);
    if (gia)
        return gia;
    const nuovo = costruisci(l);
    gruppi.set(l, nuovo);
    return nuovo;
}
/**
 * Dire a questo modulo in che lingua sta scrivendo. **La chiama solo la app.**
 *
 * È uno stato di modulo, cioè la cosa che di solito non si fa, e la ragione per
 * cui qui si fa è che l'alternativa era peggio: passare la lingua come argomento
 * voleva dire toccare i trecentoquarantadue punti in cui la app formatta
 * qualcosa, e — questo è il punto — **dimenticarne uno non si sarebbe visto**.
 * Un `formatDate` rimasto senza lingua non dà errore, non rompe niente e scrive
 * una data in italiano dentro una schermata inglese: esattamente il difetto che
 * stiamo togliendo, rimesso dentro in un punto solo e invisibile. Uno stato
 * ambientale sbaglia tutto insieme o niente, e «tutto insieme» si vede.
 *
 * Vale perché il processo che la chiama mostra **una lingua per volta a una
 * persona sola**. Un server no: lì due richieste convivono, e questa funzione
 * farebbe uscire la lingua di uno nella risposta dell'altro. Per questo l'API e
 * il pannello non la chiamano, e a impedire che comincino c'è
 * `scripts/controlla-lingua.mjs` nel cancello di lint della app.
 *
 * @param l la lingua che si sta mostrando adesso
 */
export function impostaLinguaDelProcesso(l) {
    fmt = ricava(l);
}
/**
 * Il trattino lungo che sostituisce un valore assente.
 *
 * Una cella vuota e una cella con dentro un valore mancante si assomigliano
 * troppo: la prima fa pensare a un difetto di caricamento, la seconda dice che
 * il dato non c'è. Un carattere solo, sempre lo stesso, toglie il dubbio.
 */
export const EMPTY = '—';
/**
 * Il segno meno davanti a un numero negativo: il trattino `-`, U+002D.
 *
 * Sembra una scelta da niente e non lo è, perché il carattere alternativo esiste
 * ed è più bello: U+2212, il meno tipografico, largo quanto il `+` e allineato
 * alla barra della cifra. `formatDuration` lo scriveva a mano, con accanto il
 * commento «segno meno tipografico, non il trattino», e per un po' è sembrata
 * una raffinatezza. Era un disaccordo.
 *
 * `Intl.NumberFormat` — che formatta **tutto il resto**: soldi, decimali,
 * interi, quantità, byte — emette U+002D, in italiano come in inglese come in
 * cinese, e non si può convincere a fare altrimenti se non riscrivendone
 * l'uscita. Così nella scheda dei residui una riga a giorni usciva `-1,5
 * giorni` da `formatDecimal` e la riga sotto, a ore, `−7h 30m` da
 * `formatDuration`: due segni meno diversi, incolonnati, nella stessa scheda.
 *
 * Delle due strade si è presa quella corta. Non perché U+2212 sia sbagliato, ma
 * perché tenerlo voleva dire riscrivere l'uscita di ogni `Intl` in tre basi di
 * codice — app, API e pannello — per un guadagno che è tipografico, contro tre
 * ragioni che non lo sono:
 *
 * 1. **Il foglio di calcolo non lo legge.** La scheda spese ha il bottone che
 *    scarica il riepilogo, e un `−1,5` con U+2212 dentro un CSV arriva in Excel
 *    come testo, non come numero: la colonna non si somma. Stesso esito
 *    incollando una cifra dalla app dentro un foglio.
 * 2. **La tastiera fa il trattino.** Chi cerca `-1,5` in un campo di ricerca lo
 *    scrive con il tasto che ha, e con U+2212 non trova la riga che vede.
 * 3. **CLDR per l'italiano dice U+002D**, che è poi il motivo per cui `Intl` lo
 *    emette.
 *
 * A tenere ferma la decisione c'è `tools/controlla-segno-meno.mjs`, che gira da
 * `pnpm lint:attrezzi` e guarda due cose. Qui dentro, U+2212 non compare
 * affatto, fuori dai commenti: è il punto in cui un carattere solo diventa ogni
 * numero di ogni schermata di tre basi di codice, e un divieto che ammettesse
 * eccezioni, lì, non sarebbe un divieto. Fuori di qui, in tutto il progetto, non
 * compare con una cifra da una parte sola — «−90», «−2h» — che è la forma in cui
 * il segno rientrerebbe scritto a mano dentro un'etichetta invece che da un
 * formattatore. Con una cifra da tutte e due le parti è un intervallo, «7:00–
 * 13:00», e quello è il mestiere del trattino medio: si lascia stare.
 *
 * Accanto al cancello sta la sua prova, che rimette il difetto dentro questo
 * file — il `const sign = total < 0 ? …` che c'era davvero — e guarda il
 * cancello diventare rosso.
 */
export const SEGNO_MENO = '-';
function toDate(value) {
    if (value === null || value === undefined || value === '')
        return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}
function toNumber(value) {
    if (value === null || value === undefined || value === '')
        return null;
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
}
// ---------------------------------------------------------------- date e ore
/** `21/03/2026`. Mai il formato americano, mai l'ISO a schermo. */
export function formatDate(value) {
    const d = toDate(value);
    return d ? fmt.raddrizza(fmt.data.format(d)) : EMPTY;
}
/**
 * `21/03`, `21 Mar`, `03/21`. La stessa data di `formatDate` senza l'anno, per
 * i due capi di un periodo che stanno su una riga sola. Vedi
 * `DATA_GIORNO_MESE`.
 */
export function formatDayMonth(value) {
    const d = toDate(value);
    return d ? fmt.raddrizza(fmt.giornoMese.format(d)) : EMPTY;
}
/** `21 marzo 2026`. Per intestazioni e testi, non per colonne. */
export function formatDateLong(value) {
    const d = toDate(value);
    return d ? fmt.dataLunga(d) : EMPTY;
}
/** `08:35`. Sempre a 24 ore: `hourCycle: 'h23'` e non `hour12: false`, che in it-IT darebbe `24:35` a mezzanotte. */
export function formatTime(value) {
    const d = toDate(value);
    return d ? fmt.ora.format(d) : EMPTY;
}
/** `21/03/2026 08:35`. */
export function formatDateTime(value) {
    const d = toDate(value);
    return d ? `${fmt.raddrizza(fmt.data.format(d))} ${fmt.ora.format(d)}` : EMPTY;
}
/** `sabato` minuscolo come vuole l'italiano, `Saturday` maiuscolo come vuole l'inglese, `星期六` in cinese. */
export function formatWeekday(value) {
    const d = toDate(value);
    return d ? fmt.giornoSettimana(d) : EMPTY;
}
/**
 * `oggi`, `ieri`, `3 giorni fa`, e oltre la settimana la data intera.
 *
 * La distanza si misura **in giorni di calendario nel fuso aziendale**, non in
 * multipli di ventiquattro ore. Sono due cose diverse e la differenza si vede
 * proprio nei casi che contano: una timbratura delle 23:50 di ieri dista sei ore
 * da adesso ma è di *ieri*, e scrivere "poche ore fa" accanto a un giorno
 * diverso è più confuso che utile.
 *
 * Oltre i sette giorni si passa alla data assoluta: "quarantatré giorni fa" non
 * lo converte nessuno in una data, e da lì in poi il conteggio smette di essere
 * un'informazione.
 *
 * ## Le cinque parole non stanno più scritte qui
 *
 * Erano `'oggi'`, `'ieri'`, `'domani'` e due modelli con dentro il numero, e in
 * inglese uscivano tali e quali: l'unico punto di tutto il modulo in cui la
 * lingua sbagliata **si vedeva** invece di leggersi male in silenzio.
 * `Intl.RelativeTimeFormat` con `numeric: 'auto'` dice le stesse cinque cose in
 * tutte e tre le lingue — `oggi` / `today` / `今天` — e le dice dal CLDR, cioè
 * dalla stessa tabella da cui vengono i mesi e i giorni della settimana qui
 * sopra: una traduzione in meno da tenere, e nessun cinese scritto da noi.
 *
 * In italiano cambia una parola: «tra 3 giorni» dove prima c'era «fra 3 giorni».
 * Sono la stessa parola e il CLDR ha scelto la prima; non vale un'eccezione
 * scritta a mano, che sarebbe la sesta stringa da tradurre.
 */
export function formatRelativeDay(value, now = new Date()) {
    const d = toDate(value);
    const ref = toDate(now);
    if (!d || !ref)
        return EMPTY;
    const diff = calendarDaysBetween(ref, d);
    if (diff >= -7 && diff <= 7)
        return fmt.relativo.format(diff, 'day');
    return formatDate(d);
}
/**
 * Giorni di calendario fra due istanti, nel fuso aziendale. Positivo se `to` è
 * dopo `from`.
 *
 * Passa dalle stringhe `aaaa-mm-gg` invece che dalla sottrazione degli istanti,
 * ed è l'unico modo corretto: il giorno del cambio d'ora legale dura 23 o 25
 * ore, e una divisione per 86 400 000 sbaglia di uno proprio in quei due giorni
 * l'anno — cioè quando nessuno lo sta provando.
 */
export function calendarDaysBetween(from, to) {
    const a = Date.parse(`${dayKey(from)}T00:00:00Z`);
    const b = Date.parse(`${dayKey(to)}T00:00:00Z`);
    return Math.round((b - a) / 86_400_000);
}
/**
 * `2026-03-21` — il giorno di un istante **a Roma**.
 *
 * È la chiave con cui si raggruppa qualunque cosa per giornata. Il giorno di una
 * timbratura è il giorno a Roma e non quello in UTC: fra le 00:00 e le 01:00 (le
 * 02:00 d'estate) i due differiscono, ed è esattamente l'ora in cui timbra il
 * turno di notte.
 */
export function dayKey(value) {
    const d = toDate(value);
    if (!d)
        return '';
    // `en-CA` produce `aaaa-mm-gg`, che è ISO. È un espediente noto e stabile:
    // l'alternativa è `formatToParts`, cinque righe in più per lo stesso risultato.
    return d.toLocaleDateString('en-CA', { timeZone: COMPANY_ZONE });
}
/** Quanto Roma sta avanti rispetto a UTC nell'istante `t`, in millisecondi. */
function scartoDaUtc(t) {
    const parti = {};
    for (const parte of zoneFmt.formatToParts(new Date(t)))
        parti[parte.type] = parte.value;
    const muro = Date.UTC(Number(parti.year), Number(parti.month) - 1, Number(parti.day), Number(parti.hour), Number(parti.minute), Number(parti.second));
    return muro - t;
}
/**
 * L'istante della **mezzanotte di Roma** del giorno `chiave`. L'inverso di `dayKey`.
 *
 * Serve ogni volta che una chiave torna a essere un confine da mandare al
 * server, e le due strade ovvie sbagliano tutte e due. `new Date('2026-03-21')`
 * è mezzanotte UTC, che a Roma è già l'una: una finestra costruita così non
 * contiene gli eventi della prima ora del periodo. `new Date(2026, 2, 21)` è
 * mezzanotte sull'orologio del dispositivo, e a est di Roma il giorno che ne
 * esce non è nemmeno lo stesso — il calendario chiede il giorno prima di quello
 * che sta mostrando e resta fermo. Nessuno dei due dà errore: danno una risposta
 * che sembra buona.
 *
 * Una chiave che non esiste (`2026-02-30`) restituisce una data non valida, che
 * i formattatori rendono `EMPTY` invece di inventare un giorno.
 */
export function giornoDaChiave(chiave) {
    const utc = Date.parse(`${chiave}T00:00:00Z`);
    if (Number.isNaN(utc))
        return new Date(NaN);
    // Lo scarto dipende dall'istante e l'istante dipende dallo scarto: si misura
    // sulla mezzanotte UTC e si corregge. La seconda passata serve solo se fra i
    // due istanti cade un cambio d'ora — a Roma non capita, ma è una riga.
    const primo = utc - scartoDaUtc(utc);
    return new Date(dayKey(primo) === chiave ? primo : utc - scartoDaUtc(primo));
}
const UN_GIORNO_MS = 86_400_000;
/**
 * L'istante di un orario **scritto a mano all'italiana**: il giorno `2026-10-25` e l'ora
 * `02:30` letti sull'orologio di Roma, qualunque sia il fuso del PC che li ha scritti.
 *
 * Nasce dal Cruscotto (E2T6, 2026-11-01). Ogni campo data+ora del pannello costruiva
 * `new Date(anno, mese, giorno, ore, minuti)`, cioè l'orario sull'orologio **del browser**,
 * e lo mandava con `toISOString()`; ogni schermata poi lo rimostra con `formatTime`, che
 * legge Roma. Su un PC d'ufficio col fuso sbagliato — un portatile rimasto in UTC, un
 * Windows installato in inglese — chi scrive «08:00» salva le 09:00 (o le 10:00 d'estate),
 * e se ne accorge solo rileggendo la timbratura corretta: nessun errore, l'orario è «quasi».
 *
 * I due giorni dell'anno in cui l'orologio di Roma non è una funzione:
 * - **l'ora che si ripete** (ultima domenica di ottobre, le 02:00-02:59 capitano due volte):
 *   si prende la **prima**, ancora in ora legale. È quella che si legge per prima sul turno di
 *   notte, e fra due risposte giuste si sceglie quella che non sposta niente in avanti;
 * - **l'ora che manca** (ultima domenica di marzo, le 02:00-02:59 non esistono): si va
 *   **avanti**, con lo scarto di prima del salto — `02:30` diventa l'istante che a Roma si
 *   legge `03:30`. Un orario che non esiste non si rifiuta: chi l'ha scritto voleva quella notte.
 *
 * Un giorno o un'ora che non esistono (`2026-02-30`, `25:00`) restituiscono una data non
 * valida, come `giornoDaChiave`: i formattatori la rendono `EMPTY` invece di inventare.
 */
export function istanteDaOraDiRoma(giorno, ora) {
    const g = /^(\d{4})-(\d{2})-(\d{2})$/.exec(giorno);
    const o = /^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/.exec(ora);
    if (!g || !o)
        return new Date(NaN);
    const ore = Number(o[1]);
    const minuti = Number(o[2]);
    const secondi = Number(o[3] ?? 0);
    if (ore > 23 || minuti > 59 || secondi > 59)
        return new Date(NaN);
    const muro = Date.UTC(Number(g[1]), Number(g[2]) - 1, Number(g[3]), ore, minuti, secondi);
    // `Date.UTC` trabocca in silenzio: il 30 febbraio diventa il 2 marzo.
    if (new Date(muro).toISOString().slice(0, 10) !== giorno)
        return new Date(NaN);
    // Gli scarti di un giorno prima e di un giorno dopo: nessun cambio d'ora dura meno.
    const prima = scartoDaUtc(muro - UN_GIORNO_MS);
    const dopo = scartoDaUtc(muro + UN_GIORNO_MS);
    const candidati = [muro - prima, muro - dopo].sort((a, b) => a - b);
    for (const t of candidati) {
        if (t + scartoDaUtc(t) === muro)
            return new Date(t); // il primo che si legge così: la prima occorrenza
    }
    return new Date(muro - prima); // l'ora che manca: lo scarto di prima del salto porta avanti
}
/** `08:35` — l'ora di un istante **a Roma**, per riempire un campo. L'inverso di `istanteDaOraDiRoma`. */
export function oraDiRoma(value) {
    const d = toDate(value);
    if (!d)
        return '';
    const parti = {};
    for (const parte of zoneFmt.formatToParts(d))
        parti[parte.type] = parte.value;
    return `${parti.hour}:${parti.minute}`;
}
/**
 * `2026-03-21T08:35` — il valore di un `<input type="datetime-local">` per un istante, letto a
 * Roma. Il campo nativo non ha fuso: quel che ci si scrive dentro è l'orologio di Roma, e
 * `istanteDaCampoDataOra` lo rilegge allo stesso modo.
 */
export function campoDataOraDiRoma(value) {
    const d = toDate(value);
    return d ? `${dayKey(d)}T${oraDiRoma(d)}` : '';
}
/** L'istante di un valore `aaaa-mm-ggThh:mm` di un `datetime-local`, letto a Roma. */
export function istanteDaCampoDataOra(valore) {
    if (!valore)
        return new Date(NaN);
    const [giorno = '', ora = ''] = valore.split('T');
    return istanteDaOraDiRoma(giorno, ora);
}
/**
 * Un `Date` che sul **suo** orologio (quello del browser) mostra il giorno e l'ora che `value`
 * ha a Roma. Serve solo per riempire i selettori che leggono e scrivono `Date` nel fuso del
 * browser (il `DatePicker` di PrimeVue): ci si mette questo, e quel che torna indietro si
 * rilegge con `istanteDaOrologioLocale`. Non è un istante: non si manda al server.
 */
export function orologioLocaleDiRoma(value) {
    const d = toDate(value);
    if (!d)
        return null;
    const parti = {};
    for (const parte of zoneFmt.formatToParts(d))
        parti[parte.type] = parte.value;
    return new Date(Number(parti.year), Number(parti.month) - 1, Number(parti.day), Number(parti.hour), Number(parti.minute), Number(parti.second));
}
/** L'inverso: il giorno e l'ora che un selettore mostra sul suo orologio, letti come ora di Roma. */
export function istanteDaOrologioLocale(locale) {
    if (Number.isNaN(locale.getTime()))
        return new Date(NaN);
    const due = (n) => String(n).padStart(2, '0');
    return istanteDaOraDiRoma(`${String(locale.getFullYear()).padStart(4, '0')}-${due(locale.getMonth() + 1)}-${due(locale.getDate())}`, `${due(locale.getHours())}:${due(locale.getMinutes())}:${due(locale.getSeconds())}`);
}
/**
 * `2026-03-21` più `giorni` giorni, in giorni di calendario.
 *
 * L'aritmetica passa da UTC, dove ogni giorno dura ventiquattro ore: sommare
 * 86 400 000 millisecondi a un istante locale salta o ripete un'ora nei due
 * giorni dell'anno in cui cambia l'ora legale, ed è lì che una griglia di
 * calendario mostra due volte lo stesso giorno.
 *
 * Una chiave che non esiste restituisce `''`: vale anche da controllo di
 * validità per chi la riceve da un indirizzo.
 */
export function chiavePiuGiorni(chiave, giorni) {
    const utc = Date.parse(`${chiave}T00:00:00Z`);
    if (Number.isNaN(utc))
        return '';
    return new Date(utc + giorni * 86_400_000).toISOString().slice(0, 10);
}
/*
  I cinque aiuti qui sotto vivevano dentro `CalendarioView.vue`. Il 2026-10-15 è nato un
  secondo calendario (le Riunioni a mese, `CalendarioMese.vue`) e il disegno ha preteso che
  non li ricopiasse: due copie del «31 gennaio più un mese» divergono il giorno che una delle
  due si corregge, e la griglia sbagliata non dà errore — mostra il 3 marzo. Stanno accanto a
  `dayKey` e `chiavePiuGiorni` perché ragionano in **chiavi**, dove un giorno non ha fusi.
*/
/** Il giorno della settimana di una chiave, 0 = domenica. Letto in UTC, dove una chiave non ha fusi. */
export function giornoSettimana(chiave) {
    return new Date(`${chiave}T00:00:00Z`).getUTCDay();
}
/** Il lunedì della settimana di `chiave`: la settimana italiana non comincia di domenica. */
export function lunediDi(chiave) {
    return chiavePiuGiorni(chiave, -((giornoSettimana(chiave) + 6) % 7));
}
/** Il numero del giorno dentro la chiave: `2026-03-21` → 21. */
export function numeroGiorno(chiave) {
    return Number(chiave.slice(8, 10));
}
/** Il primo del mese di `chiave`, spostato di `passi` mesi. */
export function primoDelMese(chiave, passi = 0) {
    const mesi = Number(chiave.slice(0, 4)) * 12 + Number(chiave.slice(5, 7)) - 1 + passi;
    const anno = Math.floor(mesi / 12);
    return `${String(anno).padStart(4, '0')}-${String(mesi - anno * 12 + 1).padStart(2, '0')}-01`;
}
/**
 * Lo stesso giorno del mese, `passi` mesi più in là, senza debordare.
 *
 * Il 31 gennaio più un mese non è il 3 marzo: l'aritmetica di `Date` traboccherebbe
 * lì, e chi preme «pagina giù» dal 31 si ritroverebbe due giorni oltre il mese
 * che aveva chiesto.
 */
export function mesePiu(chiave, passi) {
    const primo = primoDelMese(chiave, passi);
    const ultimo = numeroGiorno(chiavePiuGiorni(primoDelMese(chiave, passi + 1), -1));
    return chiavePiuGiorni(primo, Math.min(numeroGiorno(chiave), ultimo) - 1);
}
// ------------------------------------------------------------------- durate
/**
 * `7h 30m`, mai `7,5h`.
 *
 * Nessuno pensa alle ore in decimali: davanti a `7,5h` la testa fa la
 * conversione, e davanti a `7,25h` la sbaglia. Il minuto è l'unità in cui le
 * persone ragionano di orari di lavoro, e questa funzione è il motivo per cui in
 * tutto il progetto non compare una sola divisione per 60 scritta a mano.
 *
 * Le sigle passano da `fmt.durata` e non stanno più scritte qui: `h` e `m`
 * sono inglesi, e finivano intatte dentro le schermate cinesi. Vedi
 * `UnitaDurata`.
 *
 * @param minutes durata in minuti; negativa per un saldo in difetto
 */
export function formatDuration(minutes) {
    const total = toNumber(minutes);
    if (total === null)
        return EMPTY;
    const sign = total < 0 ? SEGNO_MENO : '';
    const abs = Math.round(Math.abs(total));
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    if (h === 0)
        return `${sign}${fmt.durata.minuti(m)}`;
    if (m === 0)
        return `${sign}${fmt.durata.ore(h)}`;
    return `${sign}${fmt.durata.oreEMinuti(h, m)}`;
}
/** Le ore decimali del database (`Decimal(8,2)`) rese come durata. */
export function formatHours(hours) {
    const h = toNumber(hours);
    return h === null ? EMPTY : formatDuration(h * 60);
}
/**
 * `340 ms`, `4,2 s`, `1m 35s` — quanto è durata **un'esecuzione**.
 *
 * Sorella di `formatDuration`, e distinta da quella per un motivo preciso: là
 * l'unità è il minuto, perché si parla di orari di lavoro. Un'automazione che
 * gira in mezzo secondo, passata di lì, viene arrotondata a `0m`; e `0m` scritto
 * accanto a un esito riuscito si legge come «non è partita», che è il contrario
 * di quello che è successo. Sotto il minuto la scala giusta è quella dei
 * millisecondi, sopra l'ora torna quella delle ore.
 *
 * Le soglie sono scelte per la cifra significativa: fino a un secondo il
 * millisecondo intero, fino a dieci un decimale (`4,2 s` distingue un lavoro da
 * uno tre volte più lento, `4 s` no), oltre il secondo intero.
 *
 * @param ms durata in millisecondi, come la scrive `JobRun.durationMs`
 */
export function formatDurationMs(ms) {
    const total = toNumber(ms);
    if (total === null)
        return EMPTY;
    const sign = total < 0 ? SEGNO_MENO : '';
    const abs = Math.abs(total);
    if (abs < 1_000)
        return `${sign}${Math.round(abs)} ms`;
    if (abs < 10_000)
        return `${sign}${fmt.secondi.format(abs / 1_000)} s`;
    if (abs < 60_000)
        return `${sign}${Math.round(abs / 1_000)} s`;
    const secondi = Math.round(abs / 1_000);
    const minuti = Math.floor(secondi / 60);
    // Oltre l'ora i secondi non aggiungono niente a chi legge: un lavoro da un'ora
    // e mezza si giudica in minuti, e `formatDuration` sa già scriverli.
    if (minuti >= 60)
        return `${sign}${formatDuration(minuti)}`;
    return `${sign}${minuti}m ${String(secondi % 60).padStart(2, '0')}s`;
}
// ------------------------------------------------------------------- numeri
/** `1.234,56 €`. Simbolo dopo, separatore di migliaia sempre. */
export function formatMoney(value) {
    const n = toNumber(value);
    return n === null ? EMPTY : fmt.soldi.format(n);
}
/** `1.234,5`. Per quantità e misure, dove l'euro non c'entra. */
export function formatDecimal(value) {
    const n = toNumber(value);
    return n === null ? EMPTY : fmt.decimale.format(n);
}
/** `1.234`. Conteggi. */
export function formatInteger(value) {
    const n = toNumber(value);
    return n === null ? EMPTY : fmt.intero.format(n);
}
/** `12,5 kg`, `340 km`. Lo spazio è unificatore: unità e numero non si separano a fine riga. */
export function formatQuantity(value, unit) {
    const n = toNumber(value);
    return n === null ? EMPTY : `${fmt.decimale.format(n)}\u00A0${unit}`;
}
/**
 * `120 × 100 cm`, `180 × 120 × 120 cm`, oppure il trattino se non si sa.
 *
 * Il segno è `×` (U+00D7) e non la lettera «x», che accanto a delle cifre si
 * legge come una cifra sbagliata — `120x100` a schermo piccolo somiglia a un
 * numero solo. È lo stesso motivo per cui in produzione quella stringa scritta a
 * mano non si riusciva a leggere di sfuggita.
 *
 * Gli spazi sono unificatori: una misura è una cosa sola e non deve spezzarsi a
 * fine riga, con il `120` sopra e il `100 cm` sotto.
 *
 * Sono scritti come `\u00A0` e non battuti. Battuto, un unificatore e'
 * indistinguibile da uno spazio normale sotto gli occhi di chi rilegge: la nota
 * qui sopra afferma che ci sono, e nessuno puo' verificarlo guardando. Se uno dei
 * tre si perdesse in una modifica — basta ribattere la riga — la misura tornerebbe
 * a spezzarsi a fine riga e nessun compilatore avrebbe niente da ridire. Con
 * l'escape la promessa e il byte sono la stessa cosa, ed e' anche il motivo per cui
 * `tools/controlla-invisibili.mjs` puo' vietarli senza dare rosso a questa riga.
 *
 * L'unità si scrive una volta in fondo e non su ogni numero: `120 cm × 100 cm` è
 * la stessa informazione detta il doppio delle volte, e in una pastiglia da
 * toccare col pollice lo spazio è quello che decide se la riga va a capo.
 */
export function formatSizeCm(size) {
    const parti = [size.lengthCm, size.widthCm, size.heightCm]
        .map((v) => toNumber(v))
        .filter((v) => v !== null);
    if (parti.length === 0)
        return EMPTY;
    return `${parti.map((v) => fmt.intero.format(v)).join('\u00A0×\u00A0')}\u00A0cm`;
}
/**
 * `1,4 MB`. Multipli di 1000, non di 1024.
 *
 * È la convenzione del SI ed è quella che usano il sistema operativo e il
 * browser quando mostrano la stessa dimensione: un allegato che il browser
 * chiama 1,4 MB e il pannello chiamasse 1,3 MiB sembrerebbe un altro file.
 */
export function formatBytes(value) {
    const n = toNumber(value);
    if (n === null)
        return EMPTY;
    if (n < 1000)
        return `${fmt.intero.format(n)}\u00A0B`;
    const units = ['kB', 'MB', 'GB', 'TB'];
    let v = n / 1000;
    let i = 0;
    while (v >= 1000 && i < units.length - 1) {
        v /= 1000;
        i += 1;
    }
    return `${fmt.decimale.format(Math.round(v * 10) / 10)}\u00A0${units[i]}`;
}
// -------------------------------------------------------------------- nomi
/**
 * `Rossi Mario` — come va scritto in un elenco ordinabile.
 *
 * Il cognome davanti non è una preferenza estetica: è ciò che rende
 * l'ordinamento alfabetico della colonna utile invece che casuale. Nel discorso
 * (un saluto, un messaggio, la conferma di un'azione) si usa `personName`.
 */
export function listName(person) {
    const parts = [person.lastname, person.firstname].filter(Boolean);
    if (parts.length > 0)
        return parts.join(' ');
    return person.displayName?.trim() || EMPTY;
}
/** `Mario Rossi` — come si nomina una persona in una frase. */
export function personName(person) {
    const parts = [person.firstname, person.lastname].filter(Boolean);
    if (parts.length > 0)
        return parts.join(' ');
    return person.displayName?.trim() || EMPTY;
}
/**
 * Le iniziali per l'avatar quando la foto non c'è. Al massimo due lettere.
 *
 * `Array.from` e non `charAt`: un nome che comincia con una lettera accentata o
 * con un carattere fuori dal piano base verrebbe tagliato a metà, e mezzo
 * carattere a schermo è il genere di dettaglio che fa sembrare rotto tutto il
 * resto.
 */
export function initials(person) {
    const source = personName(person);
    if (source === EMPTY)
        return '?';
    return source
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => Array.from(word)[0] ?? '')
        .join('')
        .toUpperCase();
}
// ------------------------------------------------------------------ elenchi
/**
 * `Mario, Luca e Anna` — la congiunzione italiana, non la virgola di Oxford.
 *
 * Serve nei messaggi ("hai selezionato Mario, Luca e Anna") e nelle conferme.
 * `Intl.ListFormat` la conosce già; scriverla a mano significa sbagliare il caso
 * di due elementi, che è quello più frequente — e in cinese significa sbagliarlo
 * del tutto, perché lì il separatore non è la virgola ma `、` e la congiunzione
 * non ha spazi intorno: `Mario、Luca和Anna`.
 */
export function formatList(items) {
    return items.length === 0 ? EMPTY : fmt.elenco.format(items);
}
/**
 * `3 richieste`, `1 richiesta`, `nessuna richiesta`.
 *
 * L'italiano ha due forme e il caso zero merita una parola invece di uno zero:
 * «0 richieste in attesa» si legge come un guasto, «nessuna richiesta in attesa»
 * si legge come una buona notizia.
 */
export function pluralize(count, singular, plural, zero) {
    if (count === 0 && zero)
        return zero;
    return `${fmt.intero.format(count)} ${count === 1 ? singular : plural}`;
}
/**
 * `nessuna riunione`, `nessun viaggio`, `nessuno scarto` — il caso zero, scritto in italiano.
 *
 * ## Il difetto da cui nasce, e quanto era largo
 *
 * `IntestazionePagina` scriveva `` `nessun ${singolare}` `` e basta: in cima a ogni elenco del
 * Cruscotto, al maschile sempre. Misurato il 2026-10-15: delle trenta unità passate a quel
 * componente **più della metà sono femminili** — persona, richiesta, comunicazione, trasferta,
 * spedizione, sessione, segnalazione, registrazione, nota, giornata, sede, scadenza, sala,
 * riunione, proposta, impostazione, esecuzione, automazione — quindi il Cruscotto diceva
 * **«nessun persona»**, **«nessun richiesta»**, **«nessun riunione»**. Si è visto in una
 * fotografia della pagina delle Riunioni, dove la pagina vuota è proprio quella che si apre
 * il primo giorno.
 *
 * ## Perché il genere lo deve dire chi chiama
 *
 * Perché dalla parola non si ricava: in italiano i femminili finiscono in `-a` (richiesta) ma
 * anche in `-e` (riunione, sede), e i maschili in `-o` (viaggio) ma anche in `-e` (mezzo è in
 * `-o`, ma «contenitore» no). Nemmeno il plurale aiuta: *comunicazioni* e *viaggi* finiscono
 * tutt'e due in `-i`. Una regola che indovina sbaglierebbe **meno spesso** di adesso, e le
 * volte che sbaglia nessuno saprebbe perché.
 *
 * ## La sola cosa che si ricava: `nessun` o `nessuno`
 *
 * Quella sì, e sta qui invece che nella testa di chi chiama: al maschile vuole `nessuno`
 * davanti a `z`, `x`, `y`, `gn`, `pn`, `ps` e `s` + consonante («nessuno scarto»,
 * «nessuno zaino»), e `nessun` in tutti gli altri casi, vocale compresa («nessun evento»).
 * Al femminile è sempre `nessuna`: l'elisione — «nessun'automazione» — è corretta ma non si
 * usa qui, perché su un'intestazione si legge come un refuso.
 */
export function nessunoDi(parola, femminile = false) {
    if (femminile)
        return `nessuna ${parola}`;
    const p = parola.toLocaleLowerCase('it-IT');
    const vuoleLaO = /^(z|x|y|gn|pn|ps|s[^aeiou\s])/.test(p);
    return `${vuoleLaO ? 'nessuno' : 'nessun'} ${parola}`;
}
//# sourceMappingURL=format.js.map